import { NmLoader } from '../NmLoader';
import { ActionButton, ActionIconButton } from '../ui/ActionButton';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  IoBarChartOutline,
  IoCalendarOutline,
  IoChevronDownOutline,
  IoChevronUpOutline,
  IoCloseOutline,
  IoMicOutline,
  IoPeopleOutline,
  IoPulseOutline,
  IoSendOutline,
  IoSparklesOutline,
  IoStopCircleOutline,
  IoTrophyOutline,
  IoWarningOutline,
} from 'react-icons/io5';
import type { PageId } from '../Sidebar';
import type {
  AthleteItem,
  DashboardAiAction,
  DashboardAiAnalysis,
  DashboardAiHistoryMessage,
  DashboardAiInsight,
  DashboardAiInsights,
  CoachDashboardOverview,
  HomeData,
} from '../../services/api';
import { dashboardAi } from '../../services/api';
import { useDashboardAiVoice } from '../../hooks/useDashboardAiVoice';
import { DashboardAiGenerativeBlocks } from './DashboardAiGenerativeBlocks';
import '../../styles/dashboard-ai.css';

interface Props {
  mode: 'coach' | 'athlete';
  viewerUserId: number | null;
  athletes?: AthleteItem[];
  refreshKey?: number;
  onNavigate: (page: PageId) => void;
  onSelectAthlete?: (id: number) => void;
  onOpenWorkout?: (workoutId: number, date?: string, athleteId?: number | null) => void;
  coachOverview?: CoachDashboardOverview | null;
  athleteData?: HomeData | null;
}

type ConversationMap = Record<string, DashboardAiHistoryMessage[]>;

function localeCode(language: string): 'en' | 'fa' | 'ar' | 'tr' {
  const code = language.slice(0, 2) as 'en' | 'fa' | 'ar' | 'tr';
  return ['en', 'fa', 'ar', 'tr'].includes(code) ? code : 'en';
}

const evidenceUnits: Record<string, string> = {
  hrv_ms: 'ms',
  ftp_watts: 'W',
  eftp_watts: 'W',
  mftp_watts: 'W',
  critical_power_watts: 'W',
  pmax_watts: 'W',
  w_prime_kj: 'kJ',
  watts_per_kg: 'W/kg',
  critical_speed: 'm/s',
  maximum_aerobic_speed: 'm/s',
  running_economy: 'ml/kg/km',
  d_prime: 'm',
  running_cadence: 'spm',
  ground_contact_time: 'ms',
  stride_length: 'm',
  vertical_oscillation: 'cm',
  critical_swim_speed: 'sec/100m',
  stroke_rate: 'spm',
  distance_per_stroke: 'm',
  strokes_per_length: 'count',
  cadence_average: 'rpm',
  power_average: 'W',
  heart_rate_average: 'bpm',
  speed_average: 'km/h',
  altitude_average: 'm',
  temperature_average: '°C',
  period_duration_minutes: 'min',
  workout_duration_minutes: 'min',
};

function displayEvidenceValue(key: string, value: number | null | undefined, language: string) {
  if (value == null) return '—';
  const locale = localeCode(language);
  const number = (candidate: number) => new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(candidate);
  if (key === 'tte_seconds') {
    const minutes = value / 60;
    const unit = { fa: 'دقیقه', ar: 'د', tr: 'dk', en: 'min' }[locale];
    return `${number(minutes)} ${unit}`;
  }
  const unit = key === 'readiness' || key.includes('percent') ? '%' : key === 'running_cadence' && locale === 'fa' ? 'قدم/دقیقه' : evidenceUnits[key];
  return `${number(value)}${unit ? ` ${unit}` : ''}`;
}

function fallbackEvidenceLabel(key: string) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function displayValue(value: number | null | undefined, unit = '') {
  return value == null ? '—' : `${value}${unit}`;
}

function buildFallbackInsights({
  mode,
  coachOverview,
  athleteData,
  viewerUserId,
  athleteId,
}: Pick<Props, 'mode' | 'coachOverview' | 'athleteData' | 'viewerUserId'> & { athleteId?: number | null }): DashboardAiInsights | null {
  if (mode === 'coach' && coachOverview) {
    if (athleteId) {
      const row = coachOverview.athletes.find((athlete) => athlete.id === athleteId);
      if (!row) return null;
      const readiness = row.readiness?.score ?? null;
      const load = row.training_load;
      return {
        scope: 'athlete',
        athlete: { id: row.id, name: row.full_name },
        generated_at: new Date().toISOString(),
        confidence: row.readiness?.coverage ?? 0.5,
        evidence: {
          readiness,
          ctl: load?.ctl ?? null,
          atl: load?.atl ?? null,
          tsb: load?.tsb ?? null,
          tss_completed_percent: null,
          duration_completed_percent: row.today_workout?.compliance_percent ?? null,
        },
        suggested_prompts: ['explain_readiness', 'adjust_today', 'review_week'],
        insights: [
          {
            id: `fallback-recovery-${row.id}`,
            code: readiness == null ? 'missing_readiness' : readiness < 50 ? 'fatigue_risk' : readiness < 70 ? 'monitor_recovery' : 'recovery_stable',
            severity: readiness == null || readiness < 70 ? (readiness != null && readiness < 50 ? 'high' : 'medium') : 'positive',
            athlete_id: row.id,
            athlete_name: row.full_name,
            params: { name: row.full_name, readiness, tsb: load?.tsb ?? null },
            evidence: [{ key: 'readiness', value: readiness, unit: '%' }],
            action: { type: 'open_performance', athlete_id: row.id },
          },
          {
            id: `fallback-load-${row.id}`,
            code: load == null ? 'missing_load' : (load.tsb ?? 0) <= -20 ? 'load_high' : (load.tsb ?? 0) < -10 ? 'load_building' : 'load_balanced',
            severity: load != null && (load.tsb ?? 0) <= -20 ? 'high' : load != null && (load.tsb ?? 0) < -10 ? 'medium' : 'positive',
            athlete_id: row.id,
            athlete_name: row.full_name,
            params: { ctl: load?.ctl ?? null, atl: load?.atl ?? null, tsb: load?.tsb ?? null },
            evidence: [],
            action: { type: 'open_charts', athlete_id: row.id },
          },
          {
            id: `fallback-plan-${row.id}`,
            code: row.today_workout ? 'plan_progress' : 'no_plan',
            severity: 'positive',
            athlete_id: row.id,
            athlete_name: row.full_name,
            params: {
              tss_percent: row.today_workout?.compliance_percent ?? null,
              duration_percent: row.today_workout?.compliance_percent ?? null,
            },
            evidence: [],
            action: { type: 'open_calendar', athlete_id: row.id },
          },
        ],
      };
    }

    const priorityRows = coachOverview.athletes.filter((row) => row.status === 'at_risk' || row.status === 'monitor').slice(0, 2);
    const insights: DashboardAiInsight[] = priorityRows.map((row) => ({
      id: `fallback-roster-${row.id}`,
      code: row.status === 'at_risk' ? 'fatigue_risk' : 'monitor_recovery',
      severity: row.status === 'at_risk' ? 'high' : 'medium',
      athlete_id: row.id,
      athlete_name: row.full_name,
      params: { name: row.full_name, readiness: row.readiness?.score ?? null, tsb: row.training_load?.tsb ?? null },
      evidence: [],
      action: { type: 'open_athlete', athlete_id: row.id },
    }));
    if (insights.length < 3 && coachOverview.counts.missing > 0) {
      insights.push({
        id: 'fallback-missing',
        code: 'missing_data',
        severity: 'medium',
        athlete_id: null,
        athlete_name: null,
        params: { count: coachOverview.counts.missing },
        evidence: [],
        action: { type: 'filter_missing' },
      });
    }
    if (insights.length < 3) {
      insights.push({
        id: 'fallback-ready',
        code: coachOverview.athletes.length ? 'roster_on_track' : 'no_roster',
        severity: 'positive',
        athlete_id: null,
        athlete_name: null,
        params: { count: coachOverview.counts.ready, total: coachOverview.athletes.length },
        evidence: [],
        action: { type: 'open_roster' },
      });
    }
    return {
      scope: 'roster',
      generated_at: new Date().toISOString(),
      confidence: coachOverview.athletes.length ? 0.75 : 0.2,
      insights: insights.slice(0, 3),
      evidence: {
        at_risk_count: coachOverview.counts.at_risk,
        monitor_count: coachOverview.counts.monitor,
        ready_count: coachOverview.counts.ready,
        missing_count: coachOverview.counts.missing,
        athlete_count: coachOverview.athletes.length,
      },
      suggested_prompts: ['who_needs_attention', 'compare_load', 'plan_changes'],
    };
  }

  if (mode === 'athlete' && athleteData) {
    const readiness = athleteData.readiness?.today?.score ?? null;
    const load = athleteData.today_trends;
    const totals = athleteData.current_week_summary?.['weekly-summary'];
    const tssPercent = totals?.totalTSSPlanned
      ? Math.round((totals.totalTSS / totals.totalTSSPlanned) * 100)
      : null;
    const durationPercent = totals?.totalTimePlanned
      ? Math.round((totals.totalTime / totals.totalTimePlanned) * 100)
      : null;
    return {
      scope: 'athlete',
      athlete: viewerUserId ? { id: viewerUserId, name: '' } : null,
      generated_at: new Date().toISOString(),
      confidence: athleteData.readiness?.today?.coverage ?? 0.65,
      evidence: {
        readiness,
        ctl: load?.ctl ?? null,
        atl: load?.atl ?? null,
        tsb: load?.tsb ?? null,
        tss_completed_percent: tssPercent,
        duration_completed_percent: durationPercent,
      },
      suggested_prompts: ['explain_readiness', 'adjust_today', 'review_week'],
      insights: [
        {
          id: 'fallback-self-recovery',
          code: readiness == null ? 'missing_readiness' : readiness < 50 ? 'fatigue_risk' : readiness < 70 ? 'monitor_recovery' : 'recovery_stable',
          severity: readiness != null && readiness < 50 ? 'high' : readiness == null || readiness < 70 ? 'medium' : 'positive',
          athlete_id: viewerUserId,
          athlete_name: null,
          params: { name: '', readiness, tsb: load?.tsb ?? null },
          evidence: [],
          action: { type: 'open_performance', athlete_id: viewerUserId },
        },
        {
          id: 'fallback-self-load',
          code: load == null ? 'missing_load' : (load.tsb ?? 0) <= -20 ? 'load_high' : (load.tsb ?? 0) < -10 ? 'load_building' : (load.tsb ?? 0) >= 10 ? 'freshness_high' : 'load_balanced',
          severity: load != null && (load.tsb ?? 0) <= -20 ? 'high' : load != null && (load.tsb ?? 0) < -10 ? 'medium' : 'positive',
          athlete_id: viewerUserId,
          athlete_name: null,
          params: { ctl: load?.ctl ?? null, atl: load?.atl ?? null, tsb: load?.tsb ?? null },
          evidence: [],
          action: { type: 'open_charts', athlete_id: viewerUserId },
        },
        {
          id: 'fallback-self-plan',
          code: totals?.totalTSSPlanned ? 'plan_progress' : 'no_plan',
          severity: tssPercent != null && tssPercent < 60 ? 'medium' : 'positive',
          athlete_id: viewerUserId,
          athlete_name: null,
          params: { tss_percent: tssPercent, duration_percent: durationPercent },
          evidence: [],
          action: { type: 'open_calendar', athlete_id: viewerUserId },
        },
      ],
    };
  }
  return null;
}

export function DashboardAiExperience({
  mode,
  viewerUserId,
  athletes = [],
  refreshKey = 0,
  onNavigate,
  onSelectAthlete,
  onOpenWorkout,
  coachOverview,
  athleteData,
}: Props) {
  const { t, i18n } = useTranslation();
  const [brief, setBrief] = useState<DashboardAiInsights | null>(null);
  const [scopedData, setScopedData] = useState<DashboardAiInsights | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedAthleteId, setSelectedAthleteId] = useState<number | null>(null);
  const [input, setInput] = useState('');
  const [conversations, setConversations] = useState<ConversationMap>({});
  const [analysis, setAnalysis] = useState<DashboardAiAnalysis | null>(null);
  const [sending, setSending] = useState(false);
  const [voiceError, setVoiceError] = useState(false);
  const [evidenceExpanded, setEvidenceExpanded] = useState(false);
  const conversationRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const scopeKey = mode === 'coach' && selectedAthleteId
    ? `athlete:${selectedAthleteId}`
    : mode === 'coach'
      ? 'roster'
      : `athlete:${viewerUserId ?? 'self'}`;
  const messages = conversations[scopeKey] ?? [];
  const activeData = scopedData ?? brief;
  const activeVoiceUserId = selectedAthleteId ?? viewerUserId;
  const fallbackData = useMemo(
    () => buildFallbackInsights({ mode, coachOverview, athleteData, viewerUserId }),
    [athleteData, coachOverview, mode, viewerUserId],
  );

  const loadBrief = useCallback(async () => {
    try {
      const response = await dashboardAi.getInsights();
      setBrief(response.data.data);
      setScopedData(response.data.data);
    } catch {
      if (fallbackData) {
        setBrief(fallbackData);
        setScopedData(fallbackData);
      }
    }
  }, [fallbackData]);

  useEffect(() => {
    void loadBrief();
  }, [loadBrief, refreshKey]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useEffect(() => {
    if (!open || (!analysis && messages.length === 0 && !sending)) return;
    const conversation = conversationRef.current;
    if (!conversation) return;
    const frame = window.requestAnimationFrame(() => {
      conversation.scrollTo({ top: conversation.scrollHeight, behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [analysis, messages.length, open, sending]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const conversation = conversationRef.current;
    const shouldFollowComposer = document.activeElement === textarea;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 110)}px`;
    if (conversation && shouldFollowComposer) {
      const frame = window.requestAnimationFrame(() => {
        conversation.scrollTop = conversation.scrollHeight;
      });
      return () => window.cancelAnimationFrame(frame);
    }
  }, [input]);

  const focusComposer = useCallback(() => {
    setEvidenceExpanded(false);
    window.requestAnimationFrame(() => {
      const conversation = conversationRef.current;
      conversation?.scrollTo({ top: conversation.scrollHeight, behavior: 'smooth' });
    });
  }, []);

  const changeScope = useCallback(async (athleteId: number | null) => {
    setSelectedAthleteId(athleteId);
    setAnalysis(null);
    setEvidenceExpanded(false);
    setDrawerLoading(true);
    try {
      const response = await dashboardAi.getInsights(athleteId);
      setScopedData(response.data.data);
    } catch {
      setScopedData(
        buildFallbackInsights({ mode, coachOverview, athleteData, viewerUserId, athleteId })
        ?? brief,
      );
    } finally {
      setDrawerLoading(false);
    }
  }, [athleteData, brief, coachOverview, mode, viewerUserId]);

  const openInsight = useCallback((insight?: DashboardAiInsight) => {
    setOpen(true);
    setVoiceError(false);
    if (mode === 'coach' && insight?.athlete_id) {
      void changeScope(insight.athlete_id);
    } else {
      void changeScope(null);
    }
  }, [changeScope, mode]);

  const send = useCallback(async (prompt?: string) => {
    const text = (prompt ?? input).trim();
    if (!text || sending) return;
    const nextUser: DashboardAiHistoryMessage = { role: 'user', content: text };
    const prior = conversations[scopeKey] ?? [];
    setConversations((current) => ({ ...current, [scopeKey]: [...prior, nextUser] }));
    setInput('');
    setEvidenceExpanded(false);
    setSending(true);
    try {
      const response = await dashboardAi.analyze({
        message: text,
        user_id: selectedAthleteId ?? undefined,
        locale: localeCode(i18n.language),
        history: prior.slice(-8),
      });
      const result = response.data.data;
      setAnalysis(result);
      setConversations((current) => ({
        ...current,
        [scopeKey]: [
          ...(current[scopeKey] ?? []),
          { role: 'assistant', content: result.answer },
        ],
      }));
    } catch {
      setConversations((current) => ({
        ...current,
        [scopeKey]: [
          ...(current[scopeKey] ?? []),
          { role: 'assistant', content: t('dashboard.ai.error.analysis') },
        ],
      }));
    } finally {
      setSending(false);
    }
  }, [conversations, i18n.language, input, scopeKey, selectedAthleteId, sending, t]);

  const voice = useDashboardAiVoice({
    userId: activeVoiceUserId,
    locale: i18n.language,
    disabled: sending,
    onTranscript: (text) => {
      setInput(text);
      setVoiceError(false);
    },
    onError: () => setVoiceError(true),
  });

  const action = useCallback((item: DashboardAiAction) => {
    if (item.athlete_id) onSelectAthlete?.(item.athlete_id);
    if (item.type === 'open_workout' && item.workout_id) {
      setOpen(false);
      onOpenWorkout?.(item.workout_id, item.date, item.athlete_id);
      return;
    }
    if (item.type === 'open_calendar') onNavigate('calendar');
    if (item.type === 'open_charts' || item.type === 'open_performance') onNavigate('charts');
    if (item.type === 'open_athlete' && item.athlete_id) {
      setOpen(false);
      onSelectAthlete?.(item.athlete_id);
    }
  }, [onNavigate, onOpenWorkout, onSelectAthlete]);

  const evidenceEntries = useMemo(
    () => Object.entries(analysis?.evidence ?? activeData?.evidence ?? {})
      .filter(([, value]) => value != null),
    [activeData?.evidence, analysis?.evidence],
  );
  const visibleEvidenceEntries = evidenceExpanded ? evidenceEntries : evidenceEntries.slice(0, 4);
  const evidenceSport = analysis?.athlete?.sport ?? activeData?.athlete?.sport;

  const drawer = open && typeof document !== 'undefined'
    ? createPortal(
        <div className="dashboard-ai__portal">
          <button
            type="button"
            className="dashboard-ai__backdrop"
            aria-label={t('dashboard.ai.close')}
            onClick={() => setOpen(false)}
          />
          <aside className="dashboard-ai__drawer" aria-modal="true" role="dialog" aria-labelledby="dashboard-ai-title">
            <header className="dashboard-ai__drawer-head">
              <span className="dashboard-ai__brand-icon"><IoSparklesOutline aria-hidden /></span>
              <span>
                <strong id="dashboard-ai-title">{t('dashboard.ai.analystTitle')}</strong>
                <small>{t('dashboard.ai.analystSubtitle')}</small>
              </span>
              <ActionIconButton
                type="button"
                className="dashboard-ai__icon-button"
                onClick={() => setOpen(false)}
                label={t('dashboard.ai.close')}
                icon={<IoCloseOutline aria-hidden />}
              />
            </header>

            <div className="dashboard-ai__scope">
              {mode === 'coach' && (
                <ActionButton
                  type="button"
                  className={selectedAthleteId == null ? 'is-active' : ''}
                  onClick={() => void changeScope(null)}
                  wrap
                  aria-pressed={selectedAthleteId == null}
                ><IoPeopleOutline aria-hidden />
                  {t('dashboard.ai.scope.roster')}</ActionButton>
              )}
              {mode === 'coach' ? (
                <label className={selectedAthleteId ? 'is-active' : ''}>
                  <IoPulseOutline aria-hidden />
                  <span className="sr-only">{t('dashboard.ai.scope.athlete')}</span>
                  <select
                    value={selectedAthleteId ?? ''}
                    onChange={(event) => void changeScope(event.target.value ? Number(event.target.value) : null)}
                  >
                    <option value="">{t('dashboard.ai.scope.selectAthlete')}</option>
                    {athletes.map((athlete) => (
                      <option key={athlete.id} value={athlete.id}>{athlete.full_name}</option>
                    ))}
                  </select>
                </label>
              ) : (
                <span className="dashboard-ai__scope-self is-active">
                  <IoPulseOutline aria-hidden />
                  {t('dashboard.ai.scope.myData')}
                </span>
              )}
            </div>

            <div className="dashboard-ai__conversation" aria-live="polite" ref={conversationRef}>
              {evidenceEntries.length > 0 && (
                <section
                  className={`dashboard-ai__evidence${evidenceExpanded ? ' is-expanded' : ''}`}
                  aria-labelledby="dashboard-ai-evidence-title"
                >
                  <div className="dashboard-ai__section-title">
                    <div>
                      <strong id="dashboard-ai-evidence-title">{t('dashboard.ai.evidence.title')}</strong>
                      {evidenceSport?.discipline && (
                        <span>{t(`dashboard.ai.sport.${evidenceSport.discipline}`)}</span>
                      )}
                    </div>
                    <div className="dashboard-ai__evidence-meta">
                      <small>
                        {t('dashboard.ai.evidence.confidence', {
                          value: Math.round((analysis?.confidence ?? activeData?.confidence ?? 0) * 100),
                        })}
                      </small>
                      {evidenceEntries.length > 4 && (
                        <ActionButton
                          type="button"
                          className="dashboard-ai__evidence-toggle"
                          aria-expanded={evidenceExpanded}
                          aria-controls="dashboard-ai-evidence-grid"
                          onClick={() => setEvidenceExpanded((current) => !current)}
                          wrap
                        >{t(evidenceExpanded ? 'dashboard.ai.evidence.showLess' : 'dashboard.ai.evidence.showDetails', {
                            count: evidenceEntries.length,
                          })}
                          {evidenceExpanded ? <IoChevronUpOutline aria-hidden /> : <IoChevronDownOutline aria-hidden />}</ActionButton>
                      )}
                    </div>
                  </div>
                  <div className="dashboard-ai__evidence-grid" id="dashboard-ai-evidence-grid">
                    {visibleEvidenceEntries.map(([key, value]) => (
                      <div key={key}>
                        <small>
                          {t(`dashboard.ai.evidence.${key}`, { defaultValue: fallbackEvidenceLabel(key) })}
                        </small>
                        <strong>{displayEvidenceValue(key, value, i18n.language)}</strong>
                      </div>
                    ))}
                  </div>
                  {evidenceExpanded && (
                    <p className="dashboard-ai__disclaimer">{t('dashboard.ai.evidence.disclaimer')}</p>
                  )}
                </section>
              )}

              {drawerLoading ? (
                <NmLoader />
              ) : (
                <>
                  {activeData?.scope === 'athlete' && activeData.performance_profile && (
                    <section className="dashboard-ai__profile" aria-labelledby="dashboard-ai-profile-title">
                      <div className="dashboard-ai__profile-head">
                        <span>
                          <IoSparklesOutline aria-hidden />
                          <strong id="dashboard-ai-profile-title">{t('dashboard.ai.profile.title')}</strong>
                        </span>
                        <small>
                          {t('dashboard.ai.profile.meta', {
                            days: activeData.performance_profile.window_days,
                            sessions: activeData.performance_profile.workout_count,
                            coverage: activeData.performance_profile.sensor_coverage_percent,
                          })}
                        </small>
                      </div>

                      {activeData.performance_profile.workout_count < 4 ? (
                        <p className="dashboard-ai__profile-empty">{t('dashboard.ai.profile.insufficient')}</p>
                      ) : (
                        <div className="dashboard-ai__profile-columns">
                          {(['strengths', 'limiters'] as const).map((group) => {
                            const signals = activeData.performance_profile?.[group] ?? [];
                            const isStrength = group === 'strengths';
                            return (
                              <div className={`dashboard-ai__profile-group dashboard-ai__profile-group--${isStrength ? 'strength' : 'limiter'}`} key={group}>
                                <div className="dashboard-ai__profile-label">
                                  {isStrength ? <IoTrophyOutline aria-hidden /> : <IoWarningOutline aria-hidden />}
                                  <strong>{t(`dashboard.ai.profile.${group}`)}</strong>
                                </div>
                                {signals.length > 0 ? signals.map((signal) => (
                                  <article key={signal.code}>
                                    <strong>{t(`dashboard.ai.profile.signal.${signal.code}.title`, signal.params)}</strong>
                                    <small>{t(`dashboard.ai.profile.signal.${signal.code}.summary`, signal.params)}</small>
                                  </article>
                                )) : (
                                  <p>{t(`dashboard.ai.profile.${isStrength ? 'noStrength' : 'noLimiter'}`)}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <p className="dashboard-ai__profile-note">{t('dashboard.ai.profile.note')}</p>
                    </section>
                  )}
                  <div className="dashboard-ai__message dashboard-ai__message--assistant">
                    <span><IoSparklesOutline aria-hidden /></span>
                    <p>
                      {activeData?.scope === 'athlete' && activeData.insights[0]
                        ? `${t(`dashboard.ai.insight.${activeData.insights[0].code}.title`, activeData.insights[0].params)}\n${t(`dashboard.ai.insight.${activeData.insights[0].code}.summary`, activeData.insights[0].params)}`
                        : t(mode === 'coach' ? 'dashboard.ai.welcome.coach' : 'dashboard.ai.welcome.athlete')}
                    </p>
                  </div>
                  {messages.map((message, index) => (
                    <div
                      key={`${scopeKey}-${index}`}
                      className={`dashboard-ai__message dashboard-ai__message--${message.role}`}
                    >
                      {message.role === 'assistant' && <span><IoSparklesOutline aria-hidden /></span>}
                      <p>{message.content}</p>
                    </div>
                  ))}
                  {sending && (
                    <div className="dashboard-ai__message dashboard-ai__message--assistant is-thinking">
                      <span><IoSparklesOutline aria-hidden /></span>
                      <p>{t('dashboard.ai.thinking')}</p>
                    </div>
                  )}
                </>
              )}

              <DashboardAiGenerativeBlocks
                blocks={analysis?.blocks ?? []}
                athleteId={analysis?.athlete?.id ?? selectedAthleteId ?? viewerUserId}
                onAction={action}
              />

              {analysis?.tool_trace?.length ? (
                <div className="dashboard-ai__tool-trace">
                  <strong>{t('dashboard.ai.ui.checkedData')}</strong>
                  <div>
                    {analysis.tool_trace.map((tool) => (
                      <span className={`is-${tool.status}`} key={`${tool.name}-${tool.status}`}>
                        {t(`dashboard.ai.ui.tool.${tool.name}`, { defaultValue: tool.name.replaceAll('_', ' ') })}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {analysis?.actions?.length ? (
                <div className="dashboard-ai__actions">
                  {analysis.actions.map((item, index) => (
                    <ActionButton
                      key={`${item.type}-${index}`}
                      type="button"
                      onClick={() => action(item)}
                      wrap
                    >{item.type === 'open_calendar' ? <IoCalendarOutline aria-hidden /> : <IoBarChartOutline aria-hidden />}
                      {t(`dashboard.ai.action.${item.type}`)}</ActionButton>
                  ))}
                </div>
              ) : null}

              {messages.length === 0 && !sending && (
                <section className="dashboard-ai__prompts">
                  <strong>{t('dashboard.ai.suggested')}</strong>
                  <div>
                    {(activeData?.suggested_prompts ?? []).map((prompt) => (
                      <ActionButton
                        key={prompt}
                        type="button"
                        onClick={() => void send(t(`dashboard.ai.prompt.${prompt}`))}
                        wrap
                      >{t(`dashboard.ai.prompt.${prompt}`)}</ActionButton>
                    ))}
                  </div>
                </section>
              )}
            </div>

            <footer className="dashboard-ai__composer">
              {voiceError && <p>{t('dashboard.ai.error.voice')}</p>}
              <div>
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onFocus={focusComposer}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      void send();
                    }
                  }}
                  placeholder={t('dashboard.ai.placeholder')}
                  rows={1}
                />
                <ActionIconButton
                  type="button"
                  className={`dashboard-ai__voice${voice.isRecording ? ' is-recording' : ''}`}
                  variant={voice.isRecording ? 'danger' : 'neutral'}
                  aria-pressed={voice.isRecording}
                  onClick={voice.toggleRecording}
                  disabled={voice.isTranscribing || !activeVoiceUserId}
                  title={t(voice.isRecording ? 'dashboard.ai.voice.stop' : 'dashboard.ai.voice.start')}
                  label={t(voice.isRecording ? 'dashboard.ai.voice.stop' : 'dashboard.ai.voice.start')}
                  icon={voice.isRecording ? <IoStopCircleOutline aria-hidden /> : <IoMicOutline aria-hidden />}
                />
                <ActionIconButton
                  type="button"
                  className="dashboard-ai__send"
                  onClick={() => void send()}
                  disabled={!input.trim() || sending}
                  label={t('dashboard.ai.send')}
                  icon={<IoSendOutline aria-hidden />}
                  variant="primary"
                />
              </div>
              {voice.isRecording && <small>{t('dashboard.ai.voice.recording')}</small>}
              {voice.isTranscribing && <small><NmLoader size="sm" inline label={t('dashboard.ai.voice.transcribing')} /></small>}
            </footer>
          </aside>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      {drawer}
    </>
  );
}
